<template lang="pug">
  .kneesleeve-options
    Tabs(:tabs="['Upload Image', 'Add Rogue Design']" v-model="tabSelection")
    span.no-selection(v-if="canvasState.totalObjects > 0 && canvasState.selectedItem.type === 'none' && canvasState.tab !== 'Add Rogue Design'") {{__('No objects selected')}}
    .segment(v-if="tabSelection === 'Upload Image'")
      .image-controls(v-if="canvasState.selectedItem.type === 'img'")
        span.instruction Selected Image
        .controls
          img.uploaded(:src="canvasState.selectedItem.img" :alt="__('Selected image')" :width="60" :height="60")
          .layer-control
            span.control(v-if="!canvasState.selectedItem.last" @click="canvasCtx.moveForward('move-forward')") {{__("Move forward")}}
            span.control(v-if="!canvasState.selectedItem.first" @click="canvasCtx.moveBackwards('move-backward')") {{__("Move backward")}}
            span.control(@click="canvasCtx.remove('remove-layer')") {{__('Delete')}}
      CustomFileUpload(
        v-if="canvasState.userImages < imageLimit"
        cache-key="CUSTOM-KNEESLEEVE-IMAGE"
        :trace-image="false"
        :select-image="uploadFile"
        :resize="{width:800}"
        :show-current-and-recent="false"
        :allow-duplicates="true"
        product-type="sleeve"
      )
        template(#disclaimer)
          div
        template(#bottom)
          p {{__('All custom sales are final and cannot be returned. In addition to your transit time, please allow 7-10 business days for your custom knee sleeves to be printed. Available in CONUS 48 only.')}}
      .text(v-if="canvasState.selectedItem.type === 'text'")
        span {{__('Selected text: ')}}
        span(style="font-weight:bold") {{canvasState.selectedItem.text}}
        .text-controls(v-if="canvasState.selectedItem.type === 'text'")
          span.control(v-if="!canvasState.selectedItem.last" @click="canvasCtx.moveForward()") {{__("Move forward")}}
          span.control(v-if="!canvasState.selectedItem.first" @click="canvasCtx.moveBackwards()") {{__("Move backward")}}
          span.control(@click="canvasCtx.remove()") {{__('Delete')}}
        span {{__('Font Type: ')}}
        span.image-text(style="font-weight:bold") {{ (canvasState.selectedItem.font || '')[0].toUpperCase() + (canvasState.selectedItem.font || '').slice(1) }}
          Swatches.font-choice.segment(
            :options="fontOpts"
            :modelValue="canvasState.selectedItem.font"
            @update:modelValue="canvasCtx.updateTextFont($event)"
          )
        span {{__('Text Size: ')}}
        span.image-text(style="font-weight:bold")  {{ canvasState.selectedItem.size }}
        Swatches.segment(
          :options="[{label:'-', action: 'minus'},{label: '+', action:'plus'}].map((value) => ({'label': value.label, 'value': value.action }))"
          @update:modelValue="canvasCtx.updateTextSize($event)"
          :modelValue="canvasState.selectedItem.size")
        span {{__('Text Color')}} ({{ Object.keys(colors).length }})
        Swatches.segment(
          :options="colorOpt.filter(color => !['TekGray','Camo', 'Gold', 'Desert Tan'].includes(color.label))"
          :colors="colors"
          @update:modelValue="canvasCtx.updateTextColor($event)"
          :modelValue="canvasState.selectedItem.fill")
      RogueButton(
        v-if="canvasState.userTexts < 3"
        text="Add Text"
        color="black"
        width="full"
        label="Add Text"
        @click="addSleeveText=!addSleeveText;canvasCtx.addText()"
        style="margin-bottom:8px"
      )
    .custom-design(v-if="tabSelection === 'Add Rogue Design'")
      Swatches.segment(
        :options="design.logoOpts"
        :colors="design.rogueImages"
        label="Choose Rogue Designs"
        @update:modelValue="updateLogo($event)"
        :modelValue="design.rogueSelected.value"
      )
      Swatches.segment(
        v-if="design.rogueSelected.colors"
        :options="colorOpt.filter(color => design.rogueSelected.colors.includes(color.value))"
        @update:modelValue="design.logoColor=$event;updateLogo($event, true)"
        label="Choose logo Color"
        :modelValue="design.logoColor"
      )
    Swatches.color-selection(
      v-if="loaded"
      :options="colorOpt.filter(color => !['Dark Green', 'Gold'].includes(color.label))"
      :colors="colors"
      @update:modelValue="getColor($event)"
      label="Base Knee Sleeve Color"
      :modelValue="canvasState.selectedColor"
    )
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useMQ } from 'sisu/js/mq'
import { type FabricObject, util } from 'fabric'
import type { CanvasState } from './kneesleeve-types'
import CustomKneesleeveCanvas from './custom-kneesleeve-canvas.vue'
import {
  ROGUE_IMAGES,
  KNEE_SLEEVE_COLOR_OPTION,
  COLORS, KNEESLEEVE_SWATCH_COLORS,
} from '@/js/pages/product/custom/kneesleeve-config'
import ROGUE_DESIGNS from '@/js/pages/product/custom/rogue-designs'
import CustomFileUpload from '@/js/pages/product/custom-bar/custom-file-upload.vue'
import FONTS from '@/js/pages/product/custom/custom-fonts'

type LogoOption = {
  value: number;
  svg: string;
  type: string;
  label: string;
  url: string;
  colors?: string[];
  scale: number;
}

const props = defineProps<{
  canvasState: CanvasState,
}>()

const emit = defineEmits<{ (e: 'loaded'): void, (e: 'update:canvasState', val: CanvasState): void }>()

const mq = useMQ()

const tabSelection = ref<string>('Upload Image')
const addSleeveText = ref<boolean>(false)
const colors = ref(KNEESLEEVE_SWATCH_COLORS)
const canvasCtx = ref<null | typeof CustomKneesleeveCanvas>(null)
const loaded = ref<boolean>(false)
const rogueSelectedImage = ref<string>('')
const fontOpts = [
  { label: 'Arvo', svg: FONTS.ARVO, value: 'arvo' },
  { label: 'Arimo', svg: FONTS.ARIMO, value: 'arimo' },
  { label: 'Montserrat', svg: FONTS.MONTSERRAT, value: 'montserrat' },
]
const design = reactive<{
  logoColor: string,
  rogueImages: any,
  rogueSelected: LogoOption | 0,
  logoOpts: LogoOption[],
}>({
  logoColor     : 'black',
  rogueImages   : ROGUE_IMAGES,
  rogueSelected : 0,
  logoOpts      : [
    {
      value  : 1,
      svg    : ROGUE_DESIGNS.RBB,
      type   : 'club',
      label  : 'Barbell Club',
      url    : 'Barbell_Club_logo__twavxa.svg',
      colors : ['black', 'white'],
      scale  : 0.35,
    },
    {
      value  : 2,
      svg    : ROGUE_DESIGNS.FLAG,
      label  : 'Flag',
      type   : 'flag',
      url    : 'Flag_logo__pxfte6.svg',
      colors : ['black', 'white'],
      scale  : 0.20,
    },
    {
      value  : 3,
      svg    : ROGUE_DESIGNS.INTERNATIONAL,
      label  : 'International',
      type   : 'international',
      url    : 'Rogue_International_Logo_sst59x.svg',
      colors : ['black', 'white', 'gold', 'red'],
      scale  : 0.35,
    },
    {
      value : 4,
      svg   : ROGUE_DESIGNS.ZEUS_LOGO,
      label : 'Zeus',
      type  : 'zeus',
      url   : 'Zeus_logo__y65i9u.svg',
      scale : 0.75,
    },
  ],
})

const colorOpt = computed(() => {
  return KNEE_SLEEVE_COLOR_OPTION.map(value => ({
    imgAttr : value.imgAttr,
    label   : value.label,
    value   : value.imgAttr,
  }))
})

const imageLimit = computed(() => mq.value === 'mobile' ? 4 : 6)

watch(tabSelection, async (nval, oval) => {
  if (tabSelection.value === props.canvasState.tab) return

  if (oval === 'Add Rogue Design') {
    canvasCtx.value!.fabric.canvas.getObjects().forEach((value: FabricObject & { objType?: string }, index: number) => {
      if (index > 0) {
        canvasCtx.value!.fabric.canvas.remove(value)
      }
    })
    canvasCtx.value!.fabric.canvas.remove(canvasCtx.value!.fabric.canvas.getObjects()[props.canvasState.rImagePosition])
    const snapshotObjects = props.canvasState.customizationSnapshot
    const enlivenedObjects = await util.enlivenObjects(snapshotObjects)
    canvasCtx.value!.fabric.canvas.add(...enlivenedObjects)
    const updatedState = JSON.parse(JSON.stringify(props.canvasState)) as CanvasState
    updatedState.tab = tabSelection.value
    updatedState.rImagePosition = -1
    emit('update:canvasState', updatedState)
  } else if (oval === 'Upload Image') {
    const customizationSnapshot = JSON.parse(JSON.stringify(canvasCtx.value!.fabric.canvas
      .getObjects()
      .filter((__: FabricObject, index: number) => index > 0)
      // We need to include the custom fields in the toObject array, or else they won't be included in the json
      .map((obj: FabricObject) => obj.toObject(['id', 'objType', 'dataUrl'])))) as FabricObject[]
    canvasCtx.value!.fabric.canvas.getObjects().forEach((value: FabricObject & { objType?: string }, index: number) => {
      if (index > 0) {
        canvasCtx.value!.fabric.canvas.remove(value)
      }
    })
    if (design.rogueSelected) {
      await updateLogo(design.logoColor, true)
    }
    const updatedState = JSON.parse(JSON.stringify(props.canvasState)) as CanvasState
    updatedState.tab = tabSelection.value
    updatedState.customizationSnapshot = customizationSnapshot
    emit('update:canvasState', updatedState)
    await nextTick()
    canvasCtx.value!.setLayer({ objType: 'none' })
  }
})

function loadCanvas(canvas: typeof CustomKneesleeveCanvas) {
  canvasCtx.value = canvas
  tabSelection.value = props.canvasState.tab
  loaded.value = true
  emit('loaded')
}

function getColor(event: string) {
  const color = COLORS[event]
  const updatedState = JSON.parse(JSON.stringify(props.canvasState))
  updatedState.selectedColor = color.value
  emit('update:canvasState', updatedState)
  if (color.value === 'tigerstripewoodlandcamo' || color.value === 'tekgraycamo') {
    canvasCtx.value!.setTextureBackground(color.hex)
  } else {
    canvasCtx.value!.setBackgroundColor(COLORS[event].hex)
  }
}

async function updateLogo(event: number | string, update = false) {
  let rImage = design.rogueSelected as LogoOption
  if (!update) {
    rImage = design.logoOpts.find(swatch => swatch.value === event)!
    design.rogueSelected = rImage
  }
  if (rImage.label.toLowerCase() === 'international') {
    rImage.url = design.rogueImages[`international_${design.logoColor}`]
  }
  if (rImage?.colors && !rImage.colors.includes(design.logoColor)) {
    design.logoColor = 'black'
  }
  const value = {
    type  : rImage.type,
    url   : rImage.url,
    fill  : design.logoColor,
    scale : rImage.scale,
  }
  rogueSelectedImage.value = rImage.label
  await canvasCtx.value!.addRogueImage(value)
}

function uploadFile(src: string) {
  canvasCtx.value!.addImage(src)
}

defineExpose({
  loadCanvas,
})
</script>
<style lang="scss" scoped>

.text-controls {
  display: flex;
  position: relative;
  width: 75%;

  .control {
    padding-top: $space-xs;
    padding-bottom: $space-xs;
    padding-right: $space-s;
    cursor: pointer;
    color: #1277B7;
    text-decoration: underline;
  }

}

.image-area {
  display: flex;
  justify-content: space-around;
}

.image-controls {
  position: relative;
  border: solid $color-gray-light;
  padding-top: $space-xxs;
  padding-bottom: $space-xxs;
  margin-bottom: $space-s;

  .instruction {
    padding-left: $space-s;
    padding-bottom: $space-s;
    padding-top: $space-s;
    font-size: 12px;
  }

  .controls {
    display: flex;
    justify-content: center;
  }

  .layer-control {
    display: flex;
    flex-direction: row;
    padding-left: $size-s;
    align-items: center;
    width: 75%;

    .control {
      font-size: 12px;
      padding: $space-xxs;
      cursor: pointer;
      color: #1277B7;
      text-decoration: underline;
    }
  }

}

.controls {
  padding-bottom: $space-s;

  .layer {
    display: flex;
    justify-content: space-between;
    padding-bottom: $space-s;
    padding-left: $space-s;
    padding-right: $space-s;
  }

  .remove {
    padding-left: $space-s;
    padding-right: $space-s;
  }

}

.instruction {
  .text {
    padding-bottom: $space-s;
  }
}

.terms {
  font-size: $size-xxs;
  padding-top: $space-s;
  padding-bottom: $space-s;
}

.selection {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: $space-xxs;

  .size-selection {
    width: 70px;

    .selected {
      border: black;
    }
  }

  .thickness-selection {
    width: 120px;
  }
}

.segment {
  padding-top: $space-s;
}

.additional-info {
  position: relative;
  padding-top: $space-xs;
  padding-bottom: $space-xs;
}

.color-selection {
  padding-top: $space-xs;
  position: relative;
}

.no-selection {
  display: flex;
  padding: $space-s;
  border: solid $color-gray-light;
}

.text {
  position: relative;;
  font-size: $size-xxs;
  padding: $space-xs;
  border: solid $color-gray-light;
  margin-bottom: $space-s;
}

.image-text {
  font-size: $size-xxs;
  position: relative;
  padding-bottom: $space-s;
}

.divider {
  padding-top: $space-xxs;
  border-top: 1px solid #E9E9E9;
}

.rules {
  font-size: $size-xs;
}

.text-size {
  display: flex;
  justify-content: space-evenly;
  padding-top: $space-xs;
}

.steps {
  padding-top: $space-xs;
  padding-bottom: $space-xs;

  .step {
    font-weight: bold;
  }
}

.option {
  border-left: none;
  border-bottom: none;
}

.thumbs {
  display: flex;
  flex-direction: row;
}

.container {
  .disabled-overlay {
    height: 90px !important;
  }
}

.thumb {
  padding-right: $space-xs;
  padding-left: $space-xs;
  width: 120px;
  height: 100px;
  padding-bottom: $space-xs;
}

.uploaded {
  border: 2px solid #4A4AF4;
}

.disclaimer {
  margin-bottom: $size-s;
  display: block;
  font-size: 12px;
  line-height: 18px;
  flex-direction: column;
  align-items: flex-start;

  .terms {
    display: flex;
    align-items: center;
    margin-top: $size-xxs;
  }
}
.font-choice :deep() {
  .swatch[data-value="arvo"]{
    font-family: 'Arvo', serif;
    font-weight: 700;
  }
  .swatch[data-value="arimo"]{
    font-family: 'Arimo', sans-serif;
    font-weight: 700;
  }
  .swatch[data-value="montserrat"]{
    font-family: 'Montserrat', sans-serif;
    font-weight: 800;
  }
}

</style>
